// T031: CollectionEditorPage - Full page for editing a collection
import { useParams, useNavigate } from 'react-router-dom';
import { useUpdateCollectionGraphQL, useDeleteCollectionGraphQL, useCreateCritiqueSessionGraphQL, type HeroStyle } from '@/hooks/graphql/useCollectionMutationsGraphQL';
import { useCollectionGraphQL } from '@/graphql/hooks/useCollectionsGraphQL';
import { CollectionEditor } from '@/components/composed/CollectionEditor';
import { LoadingState, ErrorState, AIPersonaSelectionDialog, CritiqueSessionCreationDialog, HeroStyleSettingsDialog } from '@/components/composed';
import type { FeedbackType, NCommentsConfig } from '@/types/photo-rating';
import { Container } from '@/components/layout';
import { PageHeader, PageHeaderLabel, PageHeaderCommandWell } from '@/components/PageHeader';
import { Button, ArrowRightIcon, ChevronDownIcon, PlusIcon, SparklesIcon, SettingsIcon } from '@/components/atoms';
import { CollectionIcon } from '@/components/icons';
import { CollectionShareButton } from '@/components/collection';
import { useState, useEffect } from 'react';
import { useAuth } from '@/services/auth';
import { isValidGlobalId, critiqueSessionUrl, userCollectionsUrl } from '@/utils/urls';
import { useApolloClient } from '@apollo/client';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import logger from '@/utils/logger';

export function CollectionEditorPage() {
  const { slug, collectionId } = useParams<{ slug?: string; collectionId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Validate global ID format
  if (!collectionId || !isValidGlobalId(collectionId)) {
    return (
      <Container maxWidth="lg" className="py-8">
        <ErrorState
          title="Invalid Collection ID"
          message="The collection ID format is invalid. Please check the URL and try again."
        />
      </Container>
    );
  }

  // Require slug for edit sub-path
  if (!slug) {
    return (
      <Container maxWidth="lg" className="py-8">
        <ErrorState
          title="Error: Missing Slug"
          message={`Collection edit URLs require a slug parameter. Example: /c/${collectionId}/my-collection-slug/edit`}
        />
      </Container>
    );
  }

  // Fetch collection using GraphQL
  const { data: collection, isLoading, error, refetch } = useCollectionGraphQL(collectionId);
  const { updateCollection } = useUpdateCollectionGraphQL();
  const { deleteCollection, loading: deleteLoading } = useDeleteCollectionGraphQL();
  const { createCritiqueSession, loading: createSessionLoading } = useCreateCritiqueSessionGraphQL();
  const apolloClient = useApolloClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [showAIPersonaDialog, setShowAIPersonaDialog] = useState(false);
  const [isCreatingAISession, setIsCreatingAISession] = useState(false);
  const [showCreateSessionDialog, setShowCreateSessionDialog] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [showHeroStyleDialog, setShowHeroStyleDialog] = useState(false); // Feature 653

  // Initialize form when collection first loads (only once)
  useEffect(() => {
    if (collection && !isInitialized) {
      setName(collection.name);
      setDescription(collection.description || '');
      setIsInitialized(true);
    }
  }, [collection, isInitialized]);

  const handleDelete = async () => {
    if (!collection?.globalId) return;
    if (!confirm('Are you sure you want to delete this collection? This will also delete all critique sessions from this collection and their associated comments. This cannot be undone.')) return;

    await deleteCollection(collection.globalId);

    // Invalidate Apollo caches
    apolloClient.refetchQueries({ include: ['Collections', 'CritiqueSessions'] });

    // Redirect to user's collections page after deletion
    if (user?.slug) {
      navigate(userCollectionsUrl(user.slug));
    } else {
      // Fallback to home if user slug not available
      navigate('/');
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading collection..." size="lg" />;
  }

  if (error || !collection) {
    return (
      <Container maxWidth="lg" className="py-8">
        <ErrorState
          title="Collection not found"
          message={error?.message || 'Could not load collection'}
          onRetry={() => refetch()}
        />
      </Container>
    );
  }

  /// Feature 056 US6 + 649: Create new critique session with feedback options
  // Feature 651: Extended with nCommentsConfig for N Comments feedback
  const handleCreateSession = async (sessionTitle: string, feedbackOptions: FeedbackType[], nCommentsConfig?: NCommentsConfig) => {
    if (!collection?.globalId) return;

    setIsCreatingSession(true);
    try {
      const newSession = await createCritiqueSession({
        collectionId: collection.globalId,
        title: sessionTitle,
        feedbackOptions,
        nCommentsConfig,
      });

      if (newSession) {
        // Invalidate Apollo caches
        apolloClient.refetchQueries({ include: ['Collection', 'CritiqueSessions'] });

        // Close dialog and navigate to the new session
        setShowCreateSessionDialog(false);
        navigate(critiqueSessionUrl(newSession.globalId, newSession.slug));
      }
    } catch (err) {
      logger.error('CollectionEditor', 'Failed to create session:', err);
    } finally {
      setIsCreatingSession(false);
    }
  };

  // Feature 071: Create AI critique session
  const handleCreateAISession = async (
    selectedPersonaIds: string[],
    sessionTitle: string,
    sessionDescription: string
  ) => {
    if (!collection?.globalId) return;

    setIsCreatingAISession(true);
    try {
      const newSession = await createCritiqueSession({
        collectionId: collection.globalId,
        title: sessionTitle,
        description: sessionDescription || undefined,
        intent: 'ai_only',
        allowedAiPersonas: selectedPersonaIds.length > 0 ? selectedPersonaIds : undefined,
      });

      if (newSession) {
        // Invalidate Apollo caches
        apolloClient.refetchQueries({ include: ['Collection', 'CritiqueSessions'] });

        // Close dialog and navigate to the new session
        setShowAIPersonaDialog(false);
        navigate(critiqueSessionUrl(newSession.globalId, newSession.slug));
      }
    } catch (err) {
      logger.error('CollectionEditor', 'Failed to create AI session:', err);
    } finally {
      setIsCreatingAISession(false);
    }
  };

  // Handle title change for PageHeaderLabel
  const handleTitleChange = async (newTitle: string) => {
    if (!collection?.globalId) return;
    if (newTitle.trim() && newTitle.trim() !== collection.name) {
      setName(newTitle.trim());
      await updateCollection({
        id: collection.globalId,
        name: newTitle.trim()
      });
      apolloClient.refetchQueries({ include: ['Collection', 'Collections'] });
    }
  };

  // Handle subtitle change for PageHeaderLabel
  const handleSubtitleChange = async (newSubtitle: string | null) => {
    if (!collection?.globalId) return;
    const trimmedSubtitle = newSubtitle?.trim() || '';
    const currentDescription = collection.description || '';
    if (trimmedSubtitle !== currentDescription) {
      setDescription(trimmedSubtitle);
      await updateCollection({
        id: collection.globalId,
        description: trimmedSubtitle || undefined
      });
      apolloClient.refetchQueries({ include: ['Collection', 'Collections'] });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader>
        <PageHeaderLabel
          title={name}
          subtitle={description || null}
          icon={<CollectionIcon size={32} />}
          titleEditable={true}
          subtitleEditable={true}
          subtitlePlaceholder="Add a description..."
          onTitleChange={handleTitleChange}
          onSubtitleChange={handleSubtitleChange}
        />
        <PageHeaderCommandWell>
          {/* Feature 056 US6: Session navigation (T063-T067) */}
          {collection.sessions && collection.sessions.length === 1 ? (
            // Single session: show direct button
            <Button
              variant="secondary"
              size="md"
              onClick={() => collection.sessions && navigate(critiqueSessionUrl(collection.sessions[0].id, collection.sessions[0].slug))}
            >
              <ArrowRightIcon className="mr-1.5" />
              {collection.sessions[0].title}
            </Button>
          ) : collection.sessions && collection.sessions.length > 1 ? (
            // Multiple sessions: show dropdown
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button variant="secondary" size="md">
                  <ArrowRightIcon className="mr-1.5" />
                  Critiques ({collection.sessions.length})
                  <ChevronDownIcon className="ml-1.5" />
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="min-w-[280px] bg-surface-2 rounded-md shadow-lg border border-border p-1 z-50"
                  sideOffset={5}
                >
                  {collection.sessions
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map((session) => (
                      <DropdownMenu.Item
                        key={session.id}
                        className="px-3 py-2 text-sm outline-none cursor-pointer hover:bg-muted/20 rounded text-foreground"
                        onSelect={() => navigate(critiqueSessionUrl(session.id, session.slug))}
                      >
                        {session.title}
                      </DropdownMenu.Item>
                    ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          ) : (
            // No sessions: show placeholder
            <div className="px-3 py-2.5 text-md text-muted">
              No discussions yet
            </div>
          )}

          {/* Feature 080: Collection Share Button (T084-T086) */}
          <CollectionShareButton
            collectionId={collection.globalId!}
            collectionName={collection.name}
            collectionSlug={collection.slug}
            sessions={collection.sessions?.map(s => ({
              id: s.id,
              slug: s.slug,
              title: s.title,
              createdAt: s.createdAt,
            }))}
            variant="secondary"
            size="md"
          />

          {/* T067 + Feature 649: Create Critique Session button */}
          <Button
            variant="secondary"
            size="md"
            onClick={() => setShowCreateSessionDialog(true)}
            disabled={createSessionLoading || isCreatingSession}
            title="Start new discussion"
          >
            <PlusIcon className="mr-1.5" />
            Start Discussion
          </Button>

          {/* Feature 071: Create AI Critique Session button */}
          <Button
            variant="secondary"
            size="md"
            onClick={() => setShowAIPersonaDialog(true)}
            disabled={createSessionLoading || isCreatingAISession}
            title="Request AI feedback"
          >
            <SparklesIcon className="mr-1.5" />
            AI Feedback
          </Button>

          {/* Feature 653: Collection Settings button */}
          <Button
            variant="secondary"
            size="md"
            onClick={() => setShowHeroStyleDialog(true)}
            title="Collection settings"
          >
            <SettingsIcon className="mr-1.5" />
            Settings
          </Button>

          {/* Delete Collection button - separated visually */}
          <div className="border-l border-border pl-2 ml-2">
            <Button
              variant="secondary"
              size="md"
              onClick={handleDelete}
              disabled={deleteLoading}
              className="bg-danger hover:bg-danger/90 text-white"
            >
              Delete
            </Button>
          </div>
        </PageHeaderCommandWell>
      </PageHeader>

      {/* Scrollable content area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <CollectionEditor collectionId={collection.globalId!} className="w-full h-full" />
      </div>

      {/* Feature 071: AI Persona Selection Dialog */}
      <AIPersonaSelectionDialog
        open={showAIPersonaDialog}
        onClose={() => setShowAIPersonaDialog(false)}
        onConfirm={handleCreateAISession}
        collectionName={collection.name}
        isCreating={isCreatingAISession}
      />

      {/* Feature 649: Critique Session Creation Dialog with Feedback Options */}
      {/* Feature 651: Added photoCount for N Comments option */}
      <CritiqueSessionCreationDialog
        open={showCreateSessionDialog}
        onClose={() => setShowCreateSessionDialog(false)}
        onConfirm={handleCreateSession}
        collectionName={collection.name}
        photoCount={collection.photoCount}
        isCreating={isCreatingSession}
      />

      {/* Feature 653: Hero Style Settings Dialog */}
      <HeroStyleSettingsDialog
        open={showHeroStyleDialog}
        onClose={() => setShowHeroStyleDialog(false)}
        collectionId={collection.globalId!}
        currentStyle={(collection.heroStyle as HeroStyle) || 'collage'}
        onStyleChanged={() => {
          refetch();
          apolloClient.refetchQueries({ include: ['Collections'] });
        }}
      />
    </div>
  );
}
